'use client';

import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';

import { cn } from '@/lib/utils';

/**
 * SRAM switch / toggle (CRCB2B-634). Restyled from Figma `Controls` → Switch
 * (`22:31`); the same 48 x 24 track is corroborated by `Option Item`.
 *
 * Geometry: a 48 x 24 track (`h-6 w-12`), replacing shadcn's 24 x 44, with a
 * 16px thumb inset 4px on every edge. The inset is expressed as a 1px border
 * plus `p-[3px]` so the checked state — which Figma draws with no border — keeps
 * identical geometry via a transparent border rather than a second box model.
 *
 * Colors:
 *   - unchecked        white track + 1px `--input` #d6d6d6 edge, `--primary`
 *                      thumb
 *   - unchecked hover  `--muted` #f5f5f5 track + `--border-hover` #bcbcbc edge
 *   - checked          `--primary` #141414 track, white thumb, no visible edge
 *   - disabled         recolor, not a fade (matching button/input): unchecked
 *                      keeps its white track behind a `--border-disabled` edge
 *                      with a `--disabled` thumb; checked drops to a
 *                      `--disabled` #bcbcbc track with a white thumb
 *
 * The thumb widens to 20px on hover in both hover variants (`491:2962`,
 * `1756:10638`), so that squash is built. Figma's focus variants also move and
 * resize the thumb inconsistently (`1756:10621` puts an *unchecked* thumb on the
 * right at 16 x 14) — that is a design-file slip and is not built.
 *
 * Focus converges on the shared primitive treatment (`--ring` border swap + a
 * 3px ring), replacing this file's divergent `ring-2 ... ring-offset-2`.
 */
const Switch = React.forwardRef<
    React.ComponentRef<typeof SwitchPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
    <SwitchPrimitive.Root
        data-slot="switch"
        className={cn(
            'group peer inline-flex h-6 w-12 shrink-0 cursor-pointer items-center rounded-full border p-[3px] shadow-ui transition-colors outline-none',
            'border-input bg-background',
            'hover:data-[state=unchecked]:border-border-hover hover:data-[state=unchecked]:bg-muted',
            // The checked border is `transparent` only when NOT focused. Unscoped, it
            // tied with `focus-visible:border-ring` at (0,2,0) and won on ordering,
            // which made a focused checked switch the worst case of the three
            // controls: its border stayed fully transparent, so the focus indicator
            // was the ~2.46:1 ring and nothing else. Scoping lets the focus rule
            // apply, drawing a 1px `--ring` edge on the dark track.
            //
            // Geometry is unaffected — the border was always 1px, just transparent —
            // so the thumb travel described above does not change. See the fuller
            // note in `checkbox.tsx`.
            'not-focus-visible:data-[state=checked]:border-transparent data-[state=checked]:bg-primary',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            // DERIVED, not designed — Figma defines no invalid state for any
            // selection control. Taken from Text Input's 1px --border-error edge.
            'aria-invalid:border-border-error',
            // pointer-events-none keeps the hover rules from competing with the
            // disabled rules at equal specificity.
            'disabled:pointer-events-none disabled:cursor-not-allowed',
            'data-[disabled]:data-[state=unchecked]:border-border-disabled',
            'data-[disabled]:data-[state=checked]:bg-disabled',
            className
        )}
        {...props}
        ref={ref}>
        <SwitchPrimitive.Thumb
            data-slot="switch-thumb"
            className={cn(
                'pointer-events-none block size-4 rounded-full ring-0 transition-[transform,width,background-color]',
                'bg-primary data-[state=checked]:translate-x-6 data-[state=checked]:bg-background',
                // Figma squashes the thumb wider on hover; the checked travel
                // shortens by the same 4px so it still lands 4px from the edge.
                'group-hover:w-5 group-hover:data-[state=checked]:translate-x-5',
                'data-[disabled]:bg-disabled data-[disabled]:data-[state=checked]:bg-background'
            )}
        />
    </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
